<template>
  <document
    :title="$t('message.creditNote')"
    :header-items="headerItems"
    :from="creditNote.from"
    :to-label="$t('message.creditNoteCreditedTo')"
    :to="creditNote.to"
    :sub-total="creditNote.subTotal.displayValue"
    :vat-total="creditNote.vatTotal.displayValue"
    :total="creditNote.total.displayValue"
    editable-terms
    editable-notes
    @terms-change="terms => creditNote.terms = terms"
    @notes-change="notes => creditNote.notes = notes"
  >
    <template slot="table">
      <b-table :fields="tableFields" :items="creditNote.items" stacked="sm"></b-table>
    </template>
  </document>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { formatMoney, dateToHuman } from "@/helpers/helpers.js";
import { DEFAULT_CREDIT_NOTE } from "@/store/accounting/creditNotes";
import { Document } from "@/components";

export default {
  components: {
    Document
  },
  props: {
    creditNote: {
      type: Object,
      default: () => DEFAULT_CREDIT_NOTE
    },
    recurring: {
      type: Boolean,
      default: false
    },
    preview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tableFields: [
        {
          key: "description",
          label: this.$t("message.invoiceItem")
        },
        {
          key: "price",
          label: this.$t("message.invoiceItemUnitCost") + " (€)"
        },
        {
          key: "quantity",
          label: this.$t("message.invoiceItemQuantity")
        },
        {
          key: "vat",
          label: this.$t("message.invoiceItemVat")
        },
        {
          key: "total",
          label: this.$t("message.invoiceItemPrice"),
          class: "text-sm-right",
          formatter: (value, key, item) =>
            formatMoney(item.price * item.quantity)
        }
      ]
    };
  },
  methods: {
    formatMoney,
    ...mapActions([
      "fetchInvoiceItemHistory",
      "applyCustomerInvoiceItemHistory",
      "updateInvoiceLogo"
    ]),
    addRow() {
      const { items } = this.creditNote;
      const numItems = items.length;
      const latestItem = numItems > 0 ? items[numItems - 1] : null;
      const vat =
        (latestItem && latestItem.vat) ||
        (this.userSettings.invoicing &&
          this.userSettings.invoicing.defaultVatRate) ||
        "";

      this.creditNote.items.push({
        description: "",
        price: "",
        quantity: "1",
        vat
      });
    },
    deleteRow(index) {
      this.creditNote.items.splice(index, 1);
    },
    queryPreviousItems(queryString, callback) {
      if (_.isEmpty(this.applicableInvoiceItemHistory)) {
        return callback([]);
      }

      let queriedItems = _.map(
        this.applicableInvoiceItemHistory,
        (item, prop) => ({
          value: prop,
          ...item
        })
      );

      if (queryString && queryString !== "") {
        queriedItems = _.filter(
          queriedItems,
          this.createPreviousItemFilter(queryString)
        );
      }

      queriedItems = _.sortBy(queriedItems, "value");
      callback(queriedItems);
    },
    createPreviousItemFilter(queryString) {
      return item => {
        return item.value.toLowerCase().indexOf(queryString.toLowerCase()) > -1;
      };
    },
    handlePreviousItemSelected(item, row) {
      row.price = item.price;
    }
  },
  computed: {
    ...mapGetters(["invoiceLogoFile", "applicableInvoiceItemHistory"]),
    headerItems() {
      return [
        {
          label: this.$t("message.creditNoteNumber"),
          value: this.creditNote.number
        },
        {
          label: this.$t("message.invoiceNumber"),
          value: this.creditNote.invoiceNumber
        },
        {
          label: this.$t("message.dateSent"),
          value: dateToHuman(this.creditNote.sentDate)
        },
        {
          label: this.$t("message.dateDue"),
          value: this.creditNote.dueDate
        }
      ];
    },
    subTotal() {
      return this.creditNote.items.reduce(
        (total, item) => total + item.quantity * item.price,
        0
      );
    },
    vatTotal() {
      return this.creditNote.items.reduce(
        (total, item) => total + item.quantity * item.price * (item.vat / 100),
        0
      );
    },
    total() {
      return this.subTotal + this.vatTotal;
    }
  },
  filters: {
    dateToHuman
  },
  created() {
    if (!this.preview) {
      this.fetchInvoiceItemHistory();
    }
  },
  watch: {
    "creditNote.to"(value) {
      this.applyCustomerInvoiceItemHistory(value._id);

      const applicableInvoiceItemHistory = this.applicableInvoiceItemHistory;

      this.creditNote.items = this.creditNote.items.map(item => {
        item.price = _.get(
          applicableInvoiceItemHistory,
          `[${item.description}].price`,
          item.price
        );
        return item;
      });
    },
    subTotal(value) {
      this.creditNote.subTotal.amount = value;
      this.creditNote.subTotal.displayValue = formatMoney(value);
    },
    vatTotal(value) {
      this.creditNote.vatTotal.amount = value;
      this.creditNote.vatTotal.displayValue = formatMoney(value);
    },
    total(value) {
      this.creditNote.total.amount = value;
      this.creditNote.total.displayValue = formatMoney(value);
    },
    creditNote(value) {
      this.$emit("change", value);
    }
  }
};
</script>
